import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
    AlertTriangle, ArrowRight, CalendarDays, CalendarPlus, Camera, ChevronLeft, ChevronRight, Clock, Copy, Edit2,
    Globe, LayoutGrid, List, Loader2, MapPin, Plus, RefreshCw, Search, Trash2, User, UserPlus, Video, X,
} from 'lucide-react';
import { createEventOutreachApi } from '../lib/eventOutreachApi';
import { errorText } from '../lib/apiErrors';
import {
    PHASE_ACTIONS, PHASE_CHIP, STATUS_CHIP, attentionText, countdownLabel, eventTabHref, formatEventWhen,
    formatLocalDate, formatClock, metricValue, modalityLabel, percent,
} from '../lib/eventWorkspace';

const API = process.env.REACT_APP_BACKEND_URL || '';

const VIEWS = [['upcoming', 'Upcoming'], ['past', 'Past'], ['drafts', 'Drafts'], ['all', 'All']];
const PAGE_SIZE = 25;
const MODALITY_ICON = { in_person: MapPin, online: Video, hybrid: Globe };
const MODALITY_CHIP = {
    in_person: 'bg-amber-50 text-amber-700 border-amber-200',
    online: 'bg-violet-50 text-violet-700 border-violet-200',
    hybrid: 'bg-sky-50 text-sky-700 border-sky-200',
};

const chip = 'inline-flex h-[22px] shrink-0 items-center gap-1 whitespace-nowrap rounded-md border px-2 text-[11.5px] font-medium tabular-nums';

function readParams(searchParams) {
    const view = searchParams.get('view');
    return {
        view: VIEWS.some(([key]) => key === view) ? view : 'upcoming',
        scope: searchParams.get('scope') === 'everyone' ? 'everyone' : 'mine',
        layout: searchParams.get('layout') === 'list' ? 'list' : 'cards',
        q: searchParams.get('q') || '',
        type: searchParams.get('type') || '',
        modality: searchParams.get('modality') || '',
        page: Math.max(1, Number(searchParams.get('page')) || 1),
    };
}

export default function Events() {
    const { getAuthHeader } = useAuth();
    const { contactScope } = usePermissions();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const params = readParams(searchParams);

    const [workspace, setWorkspace] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [eventTypes, setEventTypes] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [query, setQuery] = useState(params.q);
    const [busyEvent, setBusyEvent] = useState(null);

    const assigned = contactScope === 'assigned' || Boolean(workspace?.effective_scope?.clamped);
    const mine = assigned || params.scope === 'mine';
    const outreachApi = useMemo(() => createEventOutreachApi(getAuthHeader), [getAuthHeader]);

    // URL state: a refresh or Back from Event Detail returns to the same view.
    const setParam = useCallback((updates) => {
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            Object.entries(updates).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '' || (key === 'page' && value === 1)) next.delete(key);
                else next.set(key, String(value));
            });
            if (!('page' in updates)) next.delete('page');
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    useEffect(() => {
        const handle = setTimeout(() => { if (query !== params.q) setParam({ q: query.trim() }); }, 250);
        return () => clearTimeout(handle);
    }, [query, params.q, setParam]);

    const typeMap = useMemo(() => {
        const map = {};
        eventTypes.forEach((item) => { map[item._id || item.id || item.event_type_id] = item; });
        return map;
    }, [eventTypes]);

    const loadEventTypes = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/admin/event-types`, { headers: getAuthHeader() });
            setEventTypes(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch {
            // Labels only: an unlabelled type still shows its stored value.
        }
    }, [getAuthHeader]);

    const loadWorkspace = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setWorkspace(await outreachApi.getWorkspace({
                view: params.view,
                // A request, not an authorization: the server clamps an assigned viewer to Mine.
                scope: assigned ? 'mine' : params.scope,
                search: params.q,
                event_type: params.type,
                modality: params.modality,
                page: params.page,
                page_size: PAGE_SIZE,
            }));
        } catch (err) {
            const status = err?.response?.status;
            setError({
                denied: status === 403,
                message: status === 403
                    ? 'You do not have access to the Events workspace.'
                    : errorText(err, 'Events could not be loaded.'),
            });
        } finally {
            setLoading(false);
        }
    }, [outreachApi, params.view, params.scope, params.q, params.type, params.modality, params.page, assigned]);

    useEffect(() => { loadEventTypes(); }, [loadEventTypes]);
    useEffect(() => { loadWorkspace(); }, [loadWorkspace]);

    const typeLabel = (value) => typeMap[value]?.name || typeMap[value]?.label || value;

    const openEvent = (eventId, tab, filters) => navigate(eventTabHref(eventId, tab, filters));

    const publish = async (event) => {
        setBusyEvent(event.event_id);
        try {
            await axios.put(`${API}/api/events/${encodeURIComponent(event.event_id)}`, { status: 'published' }, { headers: getAuthHeader() });
            toast.success(`${event.name} published · outreach is open`);
            await loadWorkspace();
        } catch (err) {
            toast.error(errorText(err, 'Failed to publish the event'));
        } finally {
            setBusyEvent(null);
        }
    };

    const runPrimary = (event) => {
        const action = PHASE_ACTIONS[event.phase] || PHASE_ACTIONS.upcoming;
        if (action.publish) return publish(event);
        return openEvent(event.event_id, action.tab);
    };

    const handleDuplicate = async (clickEvent, event) => {
        clickEvent.stopPropagation();
        try {
            // The workspace row is a projection; copy from the full event.
            const { data: source } = await axios.get(`${API}/api/events/${encodeURIComponent(event.event_id)}`, { headers: getAuthHeader() });
            const res = await axios.post(`${API}/api/events`, {
                name: `${source.name} (Copy)`,
                description: source.description || '',
                event_type: source.event_type || source.event_type_id || '',
                modality: source.modality || 'in_person',
                date: source.date || '',
                timezone: source.timezone || 'UTC',
                location: source.location || null,
            }, { headers: getAuthHeader() });
            toast.success('Event duplicated as a draft');
            const id = res.data?.event_id || res.data?.id;
            if (id) navigate(`/events/${id}`);
            else loadWorkspace();
        } catch (err) {
            toast.error(errorText(err, 'Failed to duplicate event'));
        }
    };

    const handleDelete = async (clickEvent, event) => {
        clickEvent.stopPropagation();
        if (!window.confirm(`Delete "${event.name}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/api/events/${encodeURIComponent(event.event_id)}`, { headers: getAuthHeader() });
            toast.success('Event deleted');
            loadWorkspace();
        } catch (err) {
            toast.error(errorText(err, 'Failed to delete event'));
        }
    };

    const events = workspace?.events || [];
    const tabCounts = workspace?.tab_counts || {};
    const pageInfo = workspace?.page || { page: params.page, page_size: PAGE_SIZE, total: 0, pages: 1 };
    const filtered = Boolean(params.q || params.type || params.modality);
    const attention = workspace?.needs_attention || [];
    const showControls = !(workspace && !workspace.org_has_events);

    let empty = null;
    if (!loading && !error && workspace && events.length === 0) {
        if (!workspace.org_has_events) {
            empty = { icon: CalendarDays, title: 'Plan your first event', text: 'Create a workshop, dinner or webinar, then build the guest list from your contacts. Invites, RSVPs and follow-ups are tracked per contact.', create: true };
        } else if (filtered) {
            const label = VIEWS.find(([key]) => key === params.view)?.[1].toLowerCase();
            empty = { icon: Search, title: 'No events match these filters', text: `${params.q ? `Nothing matches “${params.q}”` : 'Nothing matches'} in ${label} events.`, clear: true };
        } else if (params.view === 'upcoming') {
            empty = { icon: CalendarPlus, title: 'Nothing scheduled yet', text: 'Create a new event, or duplicate a past one to reuse its details and invite the same people.', create: true };
        } else {
            const label = VIEWS.find(([key]) => key === params.view)?.[1].toLowerCase();
            empty = { icon: CalendarDays, title: `No ${label} events`, text: 'Events show here as they move through draft, published and completed.' };
        }
    }

    const segment = (on) => `inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-[12.5px] font-medium ${on ? 'bg-white text-stone-900 shadow-[0_0_0_1px_#E4E1DB]' : 'text-stone-600 hover:text-stone-900'}`;

    return (
        <div className="min-h-screen bg-stone-50 px-3.5 py-5 sm:p-6" data-testid="events-workspace">
            <div className="mx-auto flex max-w-[1240px] flex-col gap-4 sm:gap-[18px]">
                {/* Header */}
                <div className="flex flex-wrap items-start gap-4">
                    <div className="min-w-0 flex-[1_1_280px]">
                        <h1 className="text-[26px] font-bold leading-8 tracking-tight text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Events</h1>
                        <p className="mt-1 text-[13.5px] text-stone-600">Manage your marketing events, conferences, and webinars</p>
                    </div>
                    <Button onClick={() => setShowCreate(true)} data-testid="events-create">
                        <Plus className="mr-1.5 h-4 w-4" /> Create event
                    </Button>
                </div>

                {/* Needs attention: only when something is due (#898) */}
                {!loading && !error && attention.length > 0 && (
                    <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2 rounded-[10px] border border-amber-200 bg-amber-50 px-3.5 py-2 text-[13px] text-amber-900" role="region" aria-label={mine ? 'Needs your attention' : 'Needs attention'} data-testid="events-attention">
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap font-semibold">
                            <AlertTriangle className="h-4 w-4 text-amber-700" />{mine ? 'Needs your attention' : 'Needs attention'}
                        </span>
                        {attention.map((item, index) => (
                            <span key={item.code} className="inline-flex items-center gap-3.5">
                                {index > 0 && <span aria-hidden="true" className="h-[3px] w-[3px] rounded-full bg-amber-400" />}
                                <button
                                    type="button"
                                    data-testid={`events-attention-${item.code}`}
                                    onClick={() => openEvent(item.target.event_id, item.target.tab, item.target.filters)}
                                    className="tabular-nums underline decoration-amber-300 underline-offset-[3px] hover:text-stone-900"
                                >
                                    {attentionText(item)}
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {showControls && (
                    <div className="flex flex-col gap-3">
                        <div className="flex items-end gap-5 overflow-x-auto border-b border-stone-200" role="tablist" aria-label="Event views">
                            {VIEWS.map(([key, label]) => {
                                const on = params.view === key;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        role="tab"
                                        id={`events-tab-${key}`}
                                        aria-selected={on}
                                        aria-controls="events-panel"
                                        onClick={() => setParam({ view: key })}
                                        className={`-mb-px flex h-[38px] items-center gap-1.5 whitespace-nowrap border-b-2 text-[13.5px] ${on ? 'border-stone-900 font-semibold text-stone-900' : 'border-transparent font-medium text-stone-500 hover:text-stone-800'}`}
                                    >
                                        {label}
                                        <span className={`inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums ${on ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600'}`}>{tabCounts[key] ?? 0}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <label className="flex h-[34px] min-w-[160px] max-w-[300px] flex-[1_1_220px] items-center gap-2 rounded-lg border border-stone-200 bg-white px-2.5">
                                <Search className="h-3.5 w-3.5 text-stone-400" />
                                <input
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder="Search events"
                                    aria-label="Search events"
                                    className="min-w-0 flex-1 border-0 bg-transparent text-[13px] text-stone-900 outline-none"
                                />
                            </label>
                            <select aria-label="Event type" value={params.type} onChange={(event) => setParam({ type: event.target.value })} className={`h-[34px] rounded-lg border bg-white px-2.5 text-[13px] ${params.type ? 'border-stone-900 font-medium' : 'border-stone-200'}`}>
                                <option value="">All types</option>
                                {(workspace?.facets?.event_types || []).map((value) => <option key={value} value={value}>{typeLabel(value)}</option>)}
                            </select>
                            <select aria-label="Modality" value={params.modality} onChange={(event) => setParam({ modality: event.target.value })} className={`h-[34px] rounded-lg border bg-white px-2.5 text-[13px] ${params.modality ? 'border-stone-900 font-medium' : 'border-stone-200'}`}>
                                <option value="">All modalities</option>
                                {(workspace?.facets?.modalities || []).map((value) => <option key={value} value={value}>{modalityLabel(value)}</option>)}
                            </select>
                            <div className="hidden flex-1 sm:block" />
                            {assigned ? (
                                <span title="You can see contacts you own" className="inline-flex h-[30px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-stone-200 bg-stone-100 px-2.5 text-[12.5px] text-stone-600" data-testid="events-scope-note">
                                    <User className="h-3.5 w-3.5" />Your contacts
                                </span>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <span className="whitespace-nowrap text-xs text-stone-500">Outreach</span>
                                    <div className="flex gap-0.5 rounded-lg bg-stone-100 p-0.5" role="group" aria-label="Outreach scope">
                                        <button type="button" aria-pressed={params.scope === 'mine'} onClick={() => setParam({ scope: 'mine' })} className={segment(params.scope === 'mine')}>Mine</button>
                                        <button type="button" aria-pressed={params.scope === 'everyone'} onClick={() => setParam({ scope: 'everyone' })} className={segment(params.scope === 'everyone')}>Everyone</button>
                                    </div>
                                </div>
                            )}
                            {/* Narrow screens always use cards (#898 375px). */}
                            <div className="hidden gap-0.5 rounded-lg bg-stone-100 p-0.5 md:flex" role="group" aria-label="Layout">
                                <button type="button" aria-pressed={params.layout === 'cards'} onClick={() => setParam({ layout: 'cards', page: params.page })} className={segment(params.layout === 'cards')}><LayoutGrid className="h-3.5 w-3.5" />Cards</button>
                                <button type="button" aria-pressed={params.layout === 'list'} onClick={() => setParam({ layout: 'list', page: params.page })} className={segment(params.layout === 'list')}><List className="h-3.5 w-3.5" />List</button>
                            </div>
                        </div>
                    </div>
                )}

                <div id="events-panel" role="tabpanel" aria-labelledby={`events-tab-${params.view}`} className="flex flex-col gap-4">
                    {loading && (
                        <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 310px), 1fr))' }} data-testid="events-loading" aria-busy="true">
                            {[0, 1, 2, 3, 4, 5].map((index) => (
                                <div key={index} className="flex h-[340px] flex-col gap-3 rounded-[10px] border border-stone-200 bg-white p-4">
                                    <div className="flex justify-between"><span className="h-3 w-14 rounded bg-stone-100" /><span className="h-5 w-[70px] rounded-md bg-stone-100" /></div>
                                    <span className="h-4 w-[78%] rounded bg-stone-200" />
                                    <div className="flex gap-1.5"><span className="h-5 w-16 rounded-md bg-stone-100" /><span className="h-5 w-16 rounded-md bg-stone-100" /></div>
                                    <span className="h-3 w-[52%] rounded bg-stone-100" />
                                    <span className="h-24 rounded-lg bg-stone-50" />
                                    <div className="mt-auto flex items-center justify-between"><span className="h-3 w-28 rounded bg-stone-100" /><span className="h-[30px] w-28 rounded-lg bg-stone-200" /></div>
                                </div>
                            ))}
                        </div>
                    )}

                    {!loading && error && (
                        <div className={`flex flex-col items-center gap-2.5 rounded-xl border px-6 py-12 text-center ${error.denied ? 'border-stone-200 bg-white' : 'border-red-200 bg-red-50/40'}`} role="alert" data-testid={error.denied ? 'events-denied' : 'events-error'}>
                            <AlertTriangle className={`h-5 w-5 ${error.denied ? 'text-stone-500' : 'text-red-600'}`} />
                            <p className="text-base font-semibold text-stone-900">{error.denied ? 'No access to events' : 'Couldn’t load events'}</p>
                            <p className="max-w-md text-[13px] text-stone-600">{error.message}</p>
                            {!error.denied && <Button variant="outline" size="sm" onClick={loadWorkspace}><RefreshCw className="mr-1.5 h-3.5 w-3.5" />Try again</Button>}
                        </div>
                    )}

                    {empty && (
                        <div className="flex flex-col items-center gap-2.5 rounded-xl border border-dashed border-stone-300 bg-white px-6 py-14 text-center" data-testid="events-empty">
                            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-stone-200 bg-stone-100"><empty.icon className="h-5 w-5 text-stone-600" /></div>
                            <p className="mt-1 text-[17px] font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{empty.title}</p>
                            <p className="max-w-md text-[13px] leading-relaxed text-stone-600">{empty.text}</p>
                            <div className="mt-2 flex flex-wrap justify-center gap-2">
                                {empty.create && <Button size="sm" onClick={() => setShowCreate(true)}><Plus className="mr-1.5 h-4 w-4" />Create event</Button>}
                                {empty.clear && <Button size="sm" variant="outline" onClick={() => { setQuery(''); setParam({ q: '', type: '', modality: '' }); }}><X className="mr-1.5 h-4 w-4" />Clear filters</Button>}
                            </div>
                        </div>
                    )}

                    {!loading && !error && events.length > 0 && (
                        <>
                            <div className={params.layout === 'list' ? 'md:hidden' : ''}>
                                <div className="grid items-stretch gap-3 sm:gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 310px), 1fr))' }} data-testid="events-cards">
                                    {events.map((event) => (
                                        <EventCard
                                            key={event.event_id}
                                            event={event}
                                            mine={mine}
                                            typeLabel={typeLabel}
                                            busy={busyEvent === event.event_id}
                                            onOpen={(tab, filters) => openEvent(event.event_id, tab, filters)}
                                            onPrimary={() => runPrimary(event)}
                                            onEdit={(clickEvent) => { clickEvent.stopPropagation(); navigate(`${eventTabHref(event.event_id, 'overview')}&edit=1`); }}
                                            onDuplicate={(clickEvent) => handleDuplicate(clickEvent, event)}
                                            onDelete={(clickEvent) => handleDelete(clickEvent, event)}
                                        />
                                    ))}
                                </div>
                            </div>
                            {params.layout === 'list' && (
                                <EventList events={events} typeLabel={typeLabel} busyEvent={busyEvent}
                                    onOpen={(event, tab) => openEvent(event.event_id, tab)} onPrimary={runPrimary} />
                            )}
                            <div className="flex items-center gap-2.5 text-[12.5px] text-stone-500" data-testid="events-pager">
                                <span className="flex-1 tabular-nums">
                                    Showing {(pageInfo.page - 1) * pageInfo.page_size + 1}–{Math.min(pageInfo.page * pageInfo.page_size, pageInfo.total)} of {pageInfo.total} · {pageInfo.page_size} per page
                                </span>
                                <Button variant="outline" size="sm" aria-label="Previous page" disabled={pageInfo.page <= 1} onClick={() => setParam({ page: pageInfo.page - 1 })}><ChevronLeft className="h-4 w-4" /></Button>
                                <Button variant="outline" size="sm" aria-label="Next page" disabled={pageInfo.page >= pageInfo.pages} onClick={() => setParam({ page: pageInfo.page + 1 })}><ChevronRight className="h-4 w-4" /></Button>
                            </div>
                            {workspace?.coverage && workspace.coverage.complete === false && (
                                <p className="text-xs text-amber-700" role="status" data-testid="events-partial">Some counts are partial: this organization has more events or contacts than one view reads. Narrow the view to see exact figures.</p>
                            )}
                        </>
                    )}
                </div>
            </div>

            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Create Event</DialogTitle>
                        <DialogDescription>Set up a new marketing event</DialogDescription>
                    </DialogHeader>
                    <React.Suspense fallback={<div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>}>
                        <LazyEventEditor
                            onCancel={() => setShowCreate(false)}
                            onSave={(data) => {
                                setShowCreate(false);
                                const id = data?.event_id || data?.id;
                                if (id) navigate(`/events/${id}`);
                                else loadWorkspace();
                            }}
                        />
                    </React.Suspense>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Stat({ value, label, suffix, tone, arrow }) {
    return (
        <>
            {arrow && <ChevronRight aria-hidden="true" className="mb-5 h-3.5 w-3.5 shrink-0 text-stone-300" />}
            <div className="flex min-w-0 flex-1 flex-col gap-px">
                <span className={`whitespace-nowrap text-xl font-bold leading-[26px] tracking-tight tabular-nums ${tone || 'text-stone-900'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {value}{suffix && <span className="text-xs font-semibold text-stone-500">{suffix}</span>}
                </span>
                <span className="truncate text-[11.5px] text-stone-500">{label}</span>
            </div>
        </>
    );
}

/** One card per phase; the zones stay in the same place so the grid lines up (#898 prototype). */
function EventCard({ event, mine, typeLabel, busy, onOpen, onPrimary, onEdit, onDuplicate, onDelete }) {
    const { phase, metrics, event_totals: totals, timing } = event;
    const muted = phase === 'cancelled';
    const action = PHASE_ACTIONS[phase] || PHASE_ACTIONS.upcoming;
    const ModalityIcon = MODALITY_ICON[event.modality] || MapPin;
    const capacity = event.capacity;
    const confirmedForCapacity = totals?.rsvp_confirmed ?? 0;
    const full = capacity != null && confirmedForCapacity >= capacity;
    const chips = [];
    if (phase === 'upcoming' || phase === 'today') {
        if (metrics.follow_ups_overdue) chips.push({ label: `${metrics.follow_ups_overdue} overdue`, tone: 'bg-red-50 text-red-700 border-red-200', tab: 'guests', filters: { follow_up: 'overdue' } });
        if (metrics.to_invite) chips.push({ label: `${metrics.to_invite} to invite`, tone: 'bg-stone-100 text-stone-600 border-stone-200', tab: 'guests', filters: { column: 'to_invite' } });
    } else if (phase === 'attendance' && metrics.unmarked) {
        chips.push({ label: `${metrics.unmarked} unmarked`, tone: 'bg-amber-50 text-amber-800 border-amber-200', tab: 'guests' });
    } else if (phase === 'followup' && metrics.follow_ups_overdue) {
        chips.push({ label: `${metrics.follow_ups_overdue} overdue`, tone: 'bg-red-50 text-red-700 border-red-200', tab: 'guests', filters: { follow_up: 'overdue' } });
    }
    const open = () => onOpen(phase === 'draft' ? 'overview' : action.tab);
    const stop = (handler) => (clickEvent) => { clickEvent.stopPropagation(); handler(); };

    return (
        <article
            onClick={open}
            className={`group relative flex h-full min-h-[340px] cursor-pointer flex-col gap-2.5 rounded-[10px] border border-stone-200 p-3.5 transition-colors hover:border-stone-300 sm:px-4 ${muted ? 'bg-stone-50' : 'bg-white'}`}
            data-testid={`event-card-${event.event_id}`}
            data-phase={phase}
        >
            <div className="flex h-[22px] items-center gap-2">
                <span className="whitespace-nowrap font-mono text-[11.5px] text-stone-500">{event.event_number || ''}</span>
                <span className="flex-1" />
                {/* Always visible on touch screens; on desktop they appear on hover or keyboard focus. */}
                <div className="flex gap-0.5 md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:group-focus-within:opacity-100">
                    <button type="button" title="Edit" aria-label={`Edit ${event.name}`} onClick={onEdit} className="flex h-[26px] w-[26px] items-center justify-center rounded-md text-stone-600 hover:bg-stone-100"><Edit2 className="h-3.5 w-3.5" /></button>
                    <button type="button" title="Duplicate" aria-label={`Duplicate ${event.name}`} onClick={onDuplicate} className="flex h-[26px] w-[26px] items-center justify-center rounded-md text-stone-600 hover:bg-stone-100"><Copy className="h-3.5 w-3.5" /></button>
                    <button type="button" title="Delete" aria-label={`Delete ${event.name}`} onClick={onDelete} className="flex h-[26px] w-[26px] items-center justify-center rounded-md text-red-700 hover:bg-stone-100"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                <span className={`${chip} ${STATUS_CHIP[event.status] || STATUS_CHIP.draft}`} title={event.status_raw && event.status_raw !== event.status ? `Stored as “${event.status_raw}”` : undefined}>{event.status_label}</span>
            </div>
            <h3 className={`h-5 truncate text-[15px] font-semibold leading-5 tracking-tight ${muted ? 'text-stone-500' : 'text-stone-900'}`}>{event.name}</h3>
            <div className="flex h-[22px] flex-wrap gap-1.5 overflow-hidden">
                {event.event_type && <span className={`${chip} border-stone-200 bg-stone-100 text-stone-600`}>{typeLabel(event.event_type)}</span>}
                {event.modality && <span className={`${chip} ${MODALITY_CHIP[event.modality] || 'border-stone-200 bg-stone-100 text-stone-600'}`}><ModalityIcon className="h-3 w-3" />{modalityLabel(event.modality, event.platform)}</span>}
                {phase === 'upcoming' && timing?.days_until != null && <span className={`${chip} ${timing.days_until <= 3 ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-stone-200 bg-stone-100 text-stone-600'}`}><Clock className="h-3 w-3" />{countdownLabel(timing.days_until)}</span>}
                {phase === 'today' && <span className={`${chip} border-stone-900 bg-stone-900 text-white`}><Clock className="h-3 w-3" />Today{timing?.start_time ? ` · ${formatClock(timing.start_time)}` : ''}</span>}
            </div>
            <div className="flex min-w-0 items-center gap-1.5 text-[12.5px] text-stone-600">
                <CalendarDays className="h-3.5 w-3.5 shrink-0 text-stone-500" />
                <span className={`truncate tabular-nums ${muted ? 'text-stone-400 line-through' : ''}`}>{formatEventWhen(timing)}</span>
            </div>

            <div className={`flex h-28 flex-none flex-col justify-between gap-2.5 rounded-lg border border-stone-100 p-3 ${muted ? 'bg-stone-100/60' : 'bg-stone-50'}`} data-testid="event-card-zone">
                {phase === 'draft' && <p className="my-auto text-[13px] text-stone-600">Publish to start outreach</p>}
                {phase === 'cancelled' && <p className="my-auto text-[13px] text-stone-600">Cancelled</p>}
                {phase === 'upcoming' && mine && !metrics.on_list && (
                    <div className="my-auto flex flex-col gap-1">
                        <span className="text-[13px] text-stone-600">None of your contacts yet</span>
                        <span className="text-xs tabular-nums text-stone-500">Event: {totals?.on_list ?? 0} on list · {totals?.rsvp_confirmed ?? 0} confirmed</span>
                        <button type="button" onClick={stop(() => onOpen('guests'))} className="inline-flex w-max items-center gap-1 text-[12.5px] font-medium text-stone-900 underline underline-offset-2"><UserPlus className="h-3.5 w-3.5" />Add contacts</button>
                    </div>
                )}
                {phase === 'upcoming' && !(mine && !metrics.on_list) && (
                    <>
                        <div className="flex min-w-0 items-end gap-1.5">
                            <Stat value={metrics.on_list} label="On list" />
                            <Stat arrow value={metrics.invited} label="Invited" />
                            <Stat arrow value={metrics.rsvp_confirmed} label="Confirmed" tone="text-emerald-700" suffix={capacity != null ? ` / ${capacity}` : ''} />
                        </div>
                        {capacity != null && (
                            <div className="flex flex-col gap-1.5" title="Seats are event-wide">
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="flex-1 truncate tabular-nums text-stone-700">{confirmedForCapacity} / {capacity} confirmed</span>
                                    <span className={`whitespace-nowrap ${full ? 'font-semibold text-emerald-700' : 'text-stone-500'}`}>{full ? 'Full' : `${capacity - confirmedForCapacity} seats left`}</span>
                                </div>
                                <div className="h-1.5 overflow-hidden rounded-full bg-stone-200"><div className={`h-full rounded-full ${full ? 'bg-emerald-500' : 'bg-stone-900'}`} style={{ width: `${Math.min(100, percent(confirmedForCapacity, capacity))}%` }} /></div>
                            </div>
                        )}
                    </>
                )}
                {phase === 'today' && <div className="flex items-end"><Stat value={metrics.rsvp_confirmed} label="Confirmed" tone="text-emerald-700" suffix={capacity != null ? ` / ${capacity}` : ''} /></div>}
                {phase === 'attendance' && <div className="flex items-end"><Stat value={metrics.attended} label="Attended" tone="text-emerald-700" suffix={` / ${metrics.rsvp_confirmed}`} /></div>}
                {(phase === 'followup' || phase === 'done') && (
                    <div className="flex flex-col gap-1">
                        <span className="text-[10.5px] font-semibold tracking-wider text-stone-500">{phase === 'followup' ? 'FOLLOW-UP' : 'OUTCOME'}</span>
                        <span className="text-[12.5px] leading-snug tabular-nums text-stone-700">{metrics.attended} attended · {metricValue(metrics, 'meetings_booked')} meetings booked · {metricValue(metrics, 'converted')} converted</span>
                    </div>
                )}
            </div>

            <div className="flex h-[22px] gap-1.5 overflow-hidden">
                {chips.slice(0, 2).map((item) => (
                    <button key={item.label} type="button" onClick={stop(() => onOpen(item.tab, item.filters))} className={`${chip} ${item.tone}`}>{item.label}</button>
                ))}
            </div>

            <div className="mt-auto flex min-h-[32px] items-center gap-2.5 border-t border-stone-100 pt-3">
                <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate text-xs tabular-nums text-stone-500">
                    {['attendance', 'followup', 'done'].includes(phase) && event.photos > 0 && <><Camera className="h-3.5 w-3.5" />{event.photos} photo{event.photos === 1 ? '' : 's'}</>}
                </span>
                <button
                    type="button"
                    onClick={stop(onPrimary)}
                    disabled={busy}
                    data-testid={`event-primary-${event.event_id}`}
                    className={`inline-flex h-[30px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[7px] border px-3 text-[12.5px] font-medium disabled:opacity-60 ${action.secondary ? 'border-stone-200 bg-white text-stone-900 hover:bg-stone-50' : 'border-stone-900 bg-stone-900 text-white hover:bg-stone-800'}`}
                >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}{action.label}<ArrowRight className="h-3.5 w-3.5" />
                </button>
            </div>
        </article>
    );
}

function EventList({ events, typeLabel, busyEvent, onOpen, onPrimary }) {
    const columns = 'minmax(240px,2.4fr) 130px 128px 72px 72px 100px 80px 76px 150px';
    const cell = (value, red) => (
        <span className={`text-right text-[13px] tabular-nums ${value == null ? 'text-stone-300' : red && value ? 'font-semibold text-red-700' : 'text-stone-900'}`}>{value == null ? '—' : value}</span>
    );
    return (
        <div className="hidden overflow-x-auto rounded-[10px] border border-stone-200 bg-white md:block" data-testid="events-list">
            <div className="min-w-[1040px]" role="table" aria-label="Events">
                <div role="row" className="grid h-[38px] items-center border-b border-stone-200 bg-stone-50 px-3.5 text-[10.5px] font-semibold tracking-wider text-stone-500" style={{ gridTemplateColumns: columns }}>
                    <span role="columnheader">EVENT</span><span role="columnheader">DATE</span><span role="columnheader">PHASE</span>
                    <span role="columnheader" className="text-right">ON LIST</span><span role="columnheader" className="text-right">INVITED</span>
                    <span role="columnheader" className="text-right">CONF / CAP</span><span role="columnheader" className="text-right">ATTENDED</span>
                    <span role="columnheader" className="text-right">OVERDUE</span><span role="columnheader" />
                </div>
                {events.map((event) => {
                    const { phase, metrics, timing } = event;
                    const muted = phase === 'cancelled';
                    const pre = phase === 'draft';
                    const [phaseLabel, phaseTone] = PHASE_CHIP[phase] || PHASE_CHIP.upcoming;
                    const action = PHASE_ACTIONS[phase] || PHASE_ACTIONS.upcoming;
                    const confirmed = pre || muted ? null : `${metrics.rsvp_confirmed}${event.capacity != null ? ` / ${event.capacity}` : ''}`;
                    return (
                        <div role="row" key={event.event_id} className={`grid min-h-[56px] items-center border-b border-stone-100 px-3.5 hover:bg-stone-50 ${muted ? 'bg-stone-50' : ''}`} style={{ gridTemplateColumns: columns }} data-testid={`event-row-${event.event_id}`}>
                            <div className="flex min-w-0 flex-col gap-0.5 pr-3">
                                <div className="flex min-w-0 items-center gap-2">
                                    <span className="shrink-0 font-mono text-[11px] text-stone-500">{event.event_number}</span>
                                    <button type="button" onClick={() => onOpen(event, pre ? 'overview' : action.tab)} className={`truncate text-left text-[13.5px] font-semibold hover:underline ${muted ? 'text-stone-500' : 'text-stone-900'}`}>{event.name}</button>
                                </div>
                                <span className="truncate text-xs text-stone-500">{[event.event_type && typeLabel(event.event_type), modalityLabel(event.modality, event.platform)].filter(Boolean).join(' · ')}</span>
                            </div>
                            <span className={`whitespace-nowrap text-[12.5px] tabular-nums ${muted ? 'text-stone-400 line-through' : 'text-stone-700'}`}>{timing?.relation === 'event_day' ? 'Today' : formatLocalDate(timing?.local_date) || '—'}{timing?.start_time ? ` · ${formatClock(timing.start_time)}` : ''}</span>
                            <div><span className={`${chip} ${phaseTone}`}>{phase === 'attendance' ? `${metrics.unmarked} unmarked` : phaseLabel}</span></div>
                            {cell(pre ? null : metrics.on_list)}
                            {cell(pre || muted ? null : metrics.invited)}
                            {cell(confirmed)}
                            {cell(['attendance', 'followup', 'done'].includes(phase) ? metrics.attended : null)}
                            {cell(pre || muted ? null : metrics.follow_ups_overdue, true)}
                            <div className="flex justify-end">
                                <button type="button" disabled={busyEvent === event.event_id} onClick={() => onPrimary(event)} className="inline-flex h-7 items-center whitespace-nowrap rounded-[7px] border border-stone-200 bg-white px-2.5 text-xs font-medium text-stone-900 hover:bg-stone-50 disabled:opacity-60">{action.label}</button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

const LazyEventEditor = React.lazy(() => import('./EventEditor'));
